// Módulo responsável por gerenciar balões de informações dos veículos
// Separado do controller principal para melhor organização

export class VehicleBalloonManager {
  constructor(mapController) {
    this.mapController = mapController
    this.balloonContainer = null
    this.currentBalloon = null
    this.currentClickHandler = null
    this.isProcessingClick = false
  }

  // Simple translation method using backend translations
  t(key) {
    return window.I18n?.translations?.[key] || key
  }

  showBalloon(vehicle, marker) {
    // Evitar processamento simultâneo
    if (this.isProcessingClick) return
    
    this.isProcessingClick = true
    
    try {
      this.createContainerIfNeeded()
      this.closePreviousBalloon()
      
      const balloon = this.createBalloonElement(vehicle)
      this.positionBalloon(balloon)
      this.addInteractivity(balloon, vehicle, marker)
      
      this.balloonContainer.appendChild(balloon)
      this.currentBalloon = balloon
      
      this.setupOutsideClickHandler(balloon)
      
    } finally {
      // Liberar flag após um pequeno delay
      setTimeout(() => {
        this.isProcessingClick = false
      }, 200)
    }
  }

  createContainerIfNeeded() {
    if (!this.balloonContainer) {
      this.balloonContainer = document.createElement('div')
      this.balloonContainer.className = 'custom-vehicle-balloon-container'
      this.balloonContainer.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        width: 380px;
        height: calc(100vh - 40px);
        pointer-events: none;
        z-index: 9999;
      `
      document.body.appendChild(this.balloonContainer)
    }
  }

  createBalloonElement(vehicle) {
    const balloon = document.createElement('div')
    balloon.className = 'custom-vehicle-balloon'
    balloon.innerHTML = this.generateBalloonHTML(vehicle)
    return balloon
  }

  positionBalloon(balloon) {
    balloon.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      overflow-y: auto;
      z-index: 1001;
      pointer-events: auto;
      animation: balloonSlideIn 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    `
  }

  addInteractivity(balloon, vehicle, marker) {
    // Botão fechar
    const closeBtn = balloon.querySelector('.close-balloon-btn')
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeAllBalloons())
    }

    // Botão copiar coordenadas
    const copyBtn = balloon.querySelector('.copy-coords-btn')
    if (copyBtn) {
      copyBtn.addEventListener('click', (event) => this.copyCoordinates(vehicle, event))
    }
  }

  setupOutsideClickHandler(balloon) {
    // Limpar listeners anteriores se existirem
    if (this.currentClickHandler) {
      document.removeEventListener('click', this.currentClickHandler)
    }
    
    const clickHandler = (event) => {
      // Verificar se o clique foi dentro do balão ou em um marker
      const isInsideBalloon = balloon.contains(event.target)
      const isMapRelatedClick = this.isMapRelatedElement(event.target)
      
      if (!isInsideBalloon && !isMapRelatedClick) {
        this.closeAllBalloons()
        document.removeEventListener('click', clickHandler)
        this.currentClickHandler = null
      }
    }
    
    // Armazenar referência para poder remover depois
    this.currentClickHandler = clickHandler
    
    // Aguardar 5 minutos antes de ativar o fechamento automático por clique externo
    setTimeout(() => {
      document.addEventListener('click', clickHandler)
    }, 300000)
  }

  closePreviousBalloon() {
    // Limpar listener de click anterior
    if (this.currentClickHandler) {
      document.removeEventListener('click', this.currentClickHandler)
      this.currentClickHandler = null
    }
    
    if (this.balloonContainer) {
      this.balloonContainer.innerHTML = ''
    }
    this.currentBalloon = null
  }

  closeAllBalloons() {
    this.closePreviousBalloon()
  }

  copyCoordinates(vehicle, event) {
    const coords = `${vehicle.lat}, ${vehicle.lng}`
    navigator.clipboard.writeText(coords).then(() => {
      this.showCopyFeedback(event.target.closest('.copy-coords-btn'))
    }).catch(err => {
    })
  }

  showCopyFeedback(button) {
    const copyIcon = button.querySelector('.copy-icon')
    const checkIcon = button.querySelector('.check-icon')
    
    copyIcon.classList.add('hidden')
    checkIcon.classList.remove('hidden')
    
    button.classList.add('bg-blue-50', 'dark:bg-blue-900/30', 'border-blue-300')
    button.classList.remove('bg-white', 'dark:bg-gray-700', 'border-gray-200')
    
    setTimeout(() => {
      copyIcon.classList.remove('hidden')
      checkIcon.classList.add('hidden')
      button.classList.remove('bg-blue-50', 'dark:bg-blue-900/30', 'border-blue-300')
      button.classList.add('bg-white', 'dark:bg-gray-700', 'border-gray-200')
    }, 1500)
  }

  generateBalloonHTML(vehicle) {
    const statusData = this.getVehicleStatusData(vehicle)
    
    return `
      <div class="vehicle-info-balloon bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden max-w-sm">
        <!-- Header -->
        <div class="relative bg-white dark:bg-gray-900 p-4">
          <button class="close-balloon-btn absolute top-3 right-3 w-7 h-7 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors group">
            <svg class="w-4 h-4 text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
          
          <div class="flex items-center space-x-3">
            <div class="w-12 h-12 bg-blue-500 dark:bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-1">${vehicle.plate}</h3>
              <div class="flex items-center space-x-2 text-gray-600 dark:text-gray-400 text-sm mb-2">
                <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                <span>${this.formatDriverName(vehicle.driver)}</span>
              </div>
              <div class="flex items-center space-x-2 text-gray-500 dark:text-gray-400 text-xs mb-2">
                <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>${vehicle.last_communication || this.t('last_comm_never')}</span>
              </div>
              <div class="flex gap-2">
                ${statusData.ignitionBadge}
                ${statusData.communicationBadge}
              </div>
            </div>
          </div>
        </div>

        <!-- Telemetria -->
        <div class="p-4">
          <div class="mb-4">
            <h4 class="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3 tracking-wide">${this.t('real_time_data')}</h4>
            <div class="space-y-3">
              ${this.generateTelemetryCards(vehicle)}
              ${this.generateLocationCard(vehicle)}
            </div>
          </div>
        </div>
      </div>
    `
  }

  generateTelemetryCards(vehicle) {
    // Ordem igual aos cards: Velocidade, RPM, Marcha, Torque, Pedal, Temperatura, Pressão Óleo, Odômetro, Horímetro, Satélites, Voltagem, Bateria
    const orderedItems = []

    // 1. VELOCIDADE (KM/h) - primeira posição igual aos cards
    if (vehicle.has_speed && vehicle.speed !== undefined) {
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.65 6.35,20.04C5.96,20.43 5.35,20.43 4.96,20.04C3.68,18.76 2.78,17.17 2.32,15.45L1.29,16.47C0.9,16.86 0.9,17.47 1.29,17.86C1.68,18.25 2.29,18.25 2.68,17.86L3.7,16.83C5.11,19.37 7.76,21 12,21C18.08,21 23,16.08 23,13C23,6.92 18.08,2 12,2V3Z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-gray-900 dark:text-white truncate">${parseFloat(vehicle.speed).toFixed(0)}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('speed_label')}</div>
          </div>
        </div>
      `)
    }

    // 2. RPM (segunda posição igual aos cards)
    if (vehicle.has_rpm && vehicle.rpm !== undefined) {
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.65 6.35,20.04C5.96,20.43 5.35,20.43 4.96,20.04C3.68,18.76 2.78,17.17 2.32,15.45L1.29,16.47C0.9,16.86 0.9,17.47 1.29,17.86C1.68,18.25 2.29,18.25 2.68,17.86L3.7,16.83C5.11,19.37 7.76,21 12,21C18.08,21 23,16.08 23,13C23,6.92 18.08,2 12,2V3Z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-gray-900 dark:text-white truncate">${this.formatRpmForPopup(vehicle.rpm)}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('rpm_label')}</div>
          </div>
        </div>
      `)
    }

    // 3. MARCHA (terceira posição igual aos cards)
    if (vehicle.has_gear && vehicle.gear !== undefined) {
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,2A2,2 0 0,1 14,4V8A2,2 0 0,1 12,10A2,2 0 0,1 10,8V4A2,2 0 0,1 12,2M12,11A2,2 0 0,1 14,13V17A2,2 0 0,1 12,19A2,2 0 0,1 10,17V13A2,2 0 0,1 12,11M7,9A2,2 0 0,1 9,11V15A2,2 0 0,1 7,17A2,2 0 0,1 5,15V11A2,2 0 0,1 7,9M17,9A2,2 0 0,1 19,11V15A2,2 0 0,1 17,17A2,2 0 0,1 15,15V11A2,2 0 0,1 17,9Z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-gray-900 dark:text-white truncate">${vehicle.gear || 'N'}ª</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('gear_label')}</div>
          </div>
        </div>
      `)
    }

    // 4. TORQUE (quarta posição igual aos cards)
    if (vehicle.has_torque && vehicle.torque !== undefined) {
      const torqueColor = vehicle.torque >= 80 ? 'text-red-600 dark:text-red-400' : vehicle.torque >= 60 ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'
      const torqueBg = vehicle.torque >= 80 ? 'bg-red-100 dark:bg-red-900/30' : vehicle.torque >= 60 ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 ${torqueBg} rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 ${torqueColor}" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11,2V8.5L8.5,6L7,7.5L12,12.5L17,7.5L15.5,6L13,8.5V2M22,15V17H20V19H18V17H16V15H18V13H20V15M6,15V17H4V19H2V17H0V15H2V13H4V15M12,16L7,21L8.5,22.5L11,20H13L15.5,22.5L17,21L12,16Z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold ${torqueColor} truncate">${vehicle.torque}%</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('torque_label')}</div>
          </div>
        </div>
      `)
    }

    // 5. PEDAL ACELERADOR (quinta posição igual aos cards)
    if (vehicle.has_accelerator_pedal && vehicle.accelerator_pedal !== undefined) {
      const pedalColor = vehicle.accelerator_pedal >= 80 ? 'text-red-600 dark:text-red-400' : vehicle.accelerator_pedal >= 50 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'
      const pedalBg = vehicle.accelerator_pedal >= 80 ? 'bg-red-100 dark:bg-red-900/30' : vehicle.accelerator_pedal >= 50 ? 'bg-orange-100 dark:bg-orange-900/30' : 'bg-green-100 dark:bg-green-900/30'
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 ${pedalBg} rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 ${pedalColor}" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8,2H16A2,2 0 0,1 18,4V20A2,2 0 0,1 16,22H8A2,2 0 0,1 6,20V4A2,2 0 0,1 8,2M8,4V20H16V4H8M10,6H14V10H10V6M10,12H14V18H10V12Z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold ${pedalColor} truncate">${vehicle.accelerator_pedal}%</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('pedal_label')}</div>
          </div>
        </div>
      `)
    }

    // 6. TEMPERATURA (sexta posição igual aos cards)
    if (vehicle.has_temperature && vehicle.temperature !== undefined) {
      const tempColors = this.getTemperatureColorClasses(vehicle.temperature)
      const bgClass = this.getTemperatureBgClass(vehicle.temperature)
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 ${bgClass} rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 ${tempColors.icon}" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 14a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
                <path d="M8 0a2.5 2.5 0 00-2.5 2.5v7.55a3.5 3.5 0 105 0V2.5A2.5 2.5 0 008 0zM6.5 2.5a1.5 1.5 0 113 0v7.987l.167.15a2.5 2.5 0 11-3.333 0l.166-.15V2.5z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold ${tempColors.text} truncate">${Math.round(vehicle.temperature)}°C</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('temp_label')}</div>
          </div>
        </div>
      `)
    }

    // 7. PRESSÃO DO ÓLEO (sétima posição igual aos cards)
    if (vehicle.has_oil_pressure && vehicle.oil_pressure !== undefined) {
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 14a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
                <path d="M8 0a2.5 2.5 0 00-2.5 2.5v7.55a3.5 3.5 0 105 0V2.5A2.5 2.5 0 008 0zM6.5 2.5a1.5 1.5 0 113 0v7.987l.167.15a2.5 2.5 0 11-3.333 0l.166-.15V2.5z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-gray-900 dark:text-white truncate">${vehicle.oil_pressure}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('oil_pressure_label')}</div>
          </div>
        </div>
      `)
    }

    // 8. ODÔMETRO (KM) - oitava posição igual aos cards
    if (vehicle.has_odometer && vehicle.odometer !== undefined) {
      const formattedOdo = this.formatOdometerForPopup(vehicle.odometer)
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-orange-600 dark:text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.65 6.35,20.04C5.96,20.43 5.35,20.43 4.96,20.04C3.68,18.76 2.78,17.17 2.32,15.45L1.29,16.47C0.9,16.86 0.9,17.47 1.29,17.86C1.68,18.25 2.29,18.25 2.68,17.86L3.7,16.83C5.11,19.37 7.76,21 12,21C18.08,21 23,16.08 23,13C23,6.92 18.08,2 12,2V3Z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-gray-900 dark:text-white truncate">${formattedOdo}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('odometer_label')}</div>
          </div>
        </div>
      `)
    }

    // 9. HORÍMETRO - nona posição igual aos cards
    if (vehicle.has_horimeter && vehicle.horimeter !== undefined) {
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-gray-900 dark:text-white truncate">${this.formatHorimeterForPopup(vehicle.horimeter)}h</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('horimeter_label')}</div>
          </div>
        </div>
      `)
    }

    // 10. SATÉLITES GPS - décima posição igual aos cards
    if (vehicle.gps_satellites_active != null && vehicle.gps_satellites_total != null) {
      const satelliteColor = vehicle.gps_satellites_active > 0 ? 'text-cyan-600 dark:text-cyan-400' : 'text-gray-400'
      const satelliteBg = vehicle.gps_satellites_active > 0 ? 'bg-cyan-100 dark:bg-cyan-900/30' : 'bg-gray-100 dark:bg-gray-800'
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 ${satelliteBg} rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 ${satelliteColor}" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,10.5A1.5,1.5 0 0,0 10.5,12A1.5,1.5 0 0,0 12,13.5A1.5,1.5 0 0,0 13.5,12A1.5,1.5 0 0,0 12,10.5M20.94,11C20.7,6.83 17.17,3.3 13,3.06V1H11V3.06C6.83,3.3 3.3,6.83 3.06,11H1V13H3.06C3.3,17.17 6.83,20.7 11,20.94V23H13V20.94C17.17,20.7 20.7,17.17 20.94,13H23V11H20.94M12,19A7,7 0 0,1 5,12A7,7 0 0,1 12,5A7,7 0 0,1 19,12A7,7 0 0,1 12,19Z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-gray-900 dark:text-white truncate">${vehicle.gps_satellites_active}/${vehicle.gps_satellites_total}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('satellites_label')}</div>
          </div>
        </div>
      `)
    } else if (vehicle.has_gps_satellites && vehicle.gps_satellites !== undefined) {
      // Fallback para dados FMS mais simples
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-cyan-600 dark:text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,10.5A1.5,1.5 0 0,0 10.5,12A1.5,1.5 0 0,0 12,13.5A1.5,1.5 0 0,0 13.5,12A1.5,1.5 0 0,0 12,10.5M20.94,11C20.7,6.83 17.17,3.3 13,3.06V1H11V3.06C6.83,3.3 3.3,6.83 3.06,11H1V13H3.06C3.3,17.17 6.83,20.7 11,20.94V23H13V20.94C17.17,20.7 20.7,17.17 20.94,13H23V11H20.94M12,19A7,7 0 0,1 5,12A7,7 0 0,1 12,5A7,7 0 0,1 19,12A7,7 0 0,1 12,19Z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-gray-900 dark:text-white truncate">${vehicle.gps_satellites}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('satellites_label')}</div>
          </div>
        </div>
      `)
    }

    // 11. VOLTAGEM - décima primeira posição igual aos cards
    if (vehicle.has_voltage && vehicle.voltage !== undefined) {
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 16 16">
                <path d="M11.251.068a.5.5 0 01.227.58L9.677 6.5H13a.5.5 0 01.364.843l-8 8.5a.5.5 0 01-.842-.49L6.323 9.5H3a.5.5 0 01-.364-.843l8-8.5a.5.5 0 01.615-.09z"/>
              </svg>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-gray-900 dark:text-white truncate">${parseFloat(vehicle.voltage).toFixed(1)}V</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('voltage_label')}</div>
          </div>
        </div>
      `)
    }

    // 12. BATERIA DE BACKUP - décima segunda posição igual aos cards
    if (vehicle.has_backup_battery && vehicle.backup_battery !== undefined) {
      const batteryColor = this.getBatteryColorClass(vehicle.backup_battery)
      const batteryBgClass = this.getBatteryBgClass(vehicle.backup_battery)
      orderedItems.push(`
        <div class="metric-card bg-gray-50 dark:bg-gray-800 rounded-lg p-2 flex items-center space-x-2">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 ${batteryBgClass} rounded-lg flex items-center justify-center">
              <div class="relative w-5 h-3 bg-gray-200 dark:bg-gray-700 rounded-sm border border-gray-300 dark:border-gray-600 overflow-hidden">
                <div class="absolute inset-0.5 rounded-sm overflow-hidden">
                  <div class="h-full" style="width: ${vehicle.backup_battery}%; background: ${this.getBatteryGradient(vehicle.backup_battery)} !important;"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold ${batteryColor} truncate">${vehicle.backup_battery}%</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.t('backup_label')}</div>
          </div>
        </div>
      `)
    }

    // Se não há métricas, mostrar mensagem
    if (orderedItems.length === 0) {
      return `
        <div class="flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div class="text-center">
            <div class="w-8 h-8 mx-auto mb-2 text-gray-400">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <span class="text-sm text-gray-500 dark:text-gray-400">${this.t('telemetry_not_available')}</span>
          </div>
        </div>
      `
    }

    // Montar HTML em grid de 2 colunas
    let metricsHtml = `<div class="grid grid-cols-2 gap-2">`
    orderedItems.forEach(item => {
      metricsHtml += item
    })
    metricsHtml += `</div>`

    return `<div class="space-y-2">${metricsHtml}</div>`
  }

  generateJimiStatusCard(vehicle) {
    // Só mostrar card se tiver dados específicos do JIMI IoT
    if (!vehicle.connection_status) {
      return ''
    }

    return `
      <div class="p-4 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg border border-blue-200 dark:border-blue-700">
        <div class="flex items-start space-x-3">
          <div class="w-10 h-10 bg-blue-500 dark:bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <h5 class="text-sm font-semibold text-blue-800 dark:text-blue-200">Status do Rastreador JIMI</h5>
            <p class="text-xs text-blue-600 dark:text-blue-300 mb-3">Informações em tempo real do dispositivo</p>
            
            <div class="grid grid-cols-2 gap-3 text-xs">
              ${this.generateJimiStatusItems(vehicle)}
            </div>
          </div>
        </div>
      </div>
    `
  }

  generateJimiStatusItems(vehicle) {
    const items = []

    // Status de conexão
    if (vehicle.connection_status) {
      const isOnline = vehicle.connection_status.toLowerCase() === 'online'
      items.push(`
        <div class="flex items-center space-x-2">
          <div class="w-2 h-2 ${isOnline ? 'bg-green-400' : 'bg-red-400'} rounded-full ${isOnline ? 'animate-pulse' : ''}"></div>
          <span class="text-blue-700 dark:text-blue-300 font-medium">${isOnline ? 'Online' : 'Offline'}</span>
        </div>
      `)
    }

    // Sinal GSM
    if (vehicle.gsm_signal_level) {
      const signalColor = this.getSignalColor(vehicle.gsm_signal_level)
      items.push(`
        <div class="flex items-center space-x-2">
          <div class="w-2 h-2 ${signalColor} rounded-full"></div>
          <span class="text-blue-700 dark:text-blue-300">Sinal ${this.capitalizeFirst(vehicle.gsm_signal_level)}</span>
        </div>
      `)
    }

    // Tipo de rede
    if (vehicle.network_type) {
      items.push(`
        <div class="flex items-center space-x-2">
          <div class="w-2 h-2 bg-purple-400 rounded-full"></div>
          <span class="text-blue-700 dark:text-blue-300">Rede ${vehicle.network_type.toUpperCase()}</span>
        </div>
      `)
    }

    // Status da bateria
    if (vehicle.battery_status) {
      const batteryColor = vehicle.battery_status.toLowerCase() === 'normal' ? 'bg-green-400' : 'bg-yellow-400'
      items.push(`
        <div class="flex items-center space-x-2">
          <div class="w-2 h-2 ${batteryColor} rounded-full"></div>
          <span class="text-blue-700 dark:text-blue-300">Bateria ${this.capitalizeFirst(vehicle.battery_status)}</span>
        </div>
      `)
    }

    return items.join('')
  }

  getSignalColor(signalLevel) {
    switch(signalLevel.toLowerCase()) {
      case 'strong': return 'bg-green-400'
      case 'medium': return 'bg-yellow-400'
      case 'weak': return 'bg-orange-400'
      default: return 'bg-gray-400'
    }
  }

  capitalizeFirst(str) {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  generateLocationCard(vehicle) {
    const lat = parseFloat(vehicle.lat)
    const lng = parseFloat(vehicle.lng)

    if (!vehicle.has_position) {
      return `
        <div class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div class="flex items-center justify-center">
            <div class="text-center">
              <div class="w-8 h-8 mx-auto mb-2 text-gray-400">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                </svg>
              </div>
              <span class="text-sm text-gray-500 dark:text-gray-400">${this.t('location_not_available')}</span>
            </div>
          </div>
        </div>
      `
    }
    
    return `
      <div class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div class="flex items-start justify-between">
          <div class="flex items-center space-x-3 min-w-0 flex-1">
            <div class="w-10 h-10 bg-blue-500 dark:bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <h5 class="text-sm font-semibold text-gray-800 dark:text-gray-200">${this.t('location')}</h5>
              <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">${this.t('gps_coordinates')}</p>
              <div class="font-mono text-sm text-gray-700 dark:text-gray-300 select-all break-all">
                ${lat.toFixed(6)}, ${lng.toFixed(6)}
              </div>
            </div>
          </div>
          <button class="copy-coords-btn ml-3 p-2.5 rounded-full bg-white dark:bg-gray-700 shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-600 transition-all group">
            <svg class="w-4 h-4 text-gray-600 dark:text-gray-300 copy-icon group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
            </svg>
            <svg class="w-4 h-4 text-green-600 dark:text-green-400 check-icon hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </button>
        </div>
      </div>
    `
  }

  getVehicleStatusData(vehicle) {
    const ignitionStatus = this.getIgnitionStatus(vehicle)
    const commStatus = this.getCommunicationStatus(vehicle)
    
    return {
      ignitionBadge: `
        <div class="inline-flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-full">
          <div class="w-1.5 h-1.5 ${ignitionStatus.dotColor} rounded-full"></div>
          <span class="text-xs font-medium text-gray-700 dark:text-gray-300">${this.t('ignition_status')} ${ignitionStatus.text}</span>
        </div>
      `,
      communicationBadge: `
        <div class="inline-flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-full">
          <div class="w-1.5 h-1.5 ${commStatus.dotColor} rounded-full"></div>
          <span class="text-xs font-medium text-gray-700 dark:text-gray-300">${commStatus.text}</span>
        </div>
      `
    }
  }

  getIgnitionStatus(vehicle) {
    const state = vehicle.vehicle_state?.toLowerCase()
    if (state === 'ignition_on' || state === 'on') {
      return { text: this.t('ignition_on'), dotColor: 'bg-green-400' }
    } else if (state === 'ignition_idle' || state === 'idle') {
      return { text: this.t('ignition_idle'), dotColor: 'bg-yellow-400' }
    } else {
      return { text: this.t('ignition_off'), dotColor: 'bg-gray-400' }
    }
  }

  getCommunicationStatus(vehicle) {
    const isOnline = vehicle.status === 'online' || vehicle.comm_status === 'ok'
    return {
      text: isOnline ? this.t('online') : this.t('offline'),
      dotColor: isOnline ? 'bg-green-400 animate-pulse' : 'bg-red-400'
    }
  }

  getTemperatureColor(temp) {
    const temperature = parseFloat(temp) || 25
    if (temperature >= 80) return 'red'
    if (temperature >= 60) return 'orange'
    if (temperature >= 40) return 'yellow'
    return 'blue'
  }

  getBatteryColor(battery) {
    const level = parseFloat(battery) || 85
    if (level > 60) return 'green'
    if (level > 30) return 'yellow'
    return 'red'
  }

  getMetricIcon(type, color) {
    const colorClass = `text-${color}-600 dark:text-${color}-400`
    const icons = {
      speedometer: `<svg class="w-3.5 h-3.5 ${colorClass}" fill="currentColor" viewBox="0 0 16 16"><path d="M8 4a.5.5 0 01.5.5V6a.5.5 0 01-1 0V4.5A.5.5 0 018 4zM3.732 5.732a.5.5 0 01.707 0l.707.707a.5.5 0 11-.708.708l-.707-.707a.5.5 0 010-.708zM2 10a.5.5 0 01.5-.5h1.586a.5.5 0 010 1H2.5A.5.5 0 012 10zm9.5 0a.5.5 0 01.5-.5h1.5a.5.5 0 010 1H12a.5.5 0 01-.5-.5zm.754-4.246a.389.389 0 00-.527-.02L7.547 9.31a.91.91 0 101.302 1.258l3.434-4.297a.389.389 0 00-.029-.518z"/></svg>`,
      thermometer: `<svg class="w-3.5 h-3.5 ${colorClass}" fill="currentColor" viewBox="0 0 16 16"><path d="M5 12.5a1.5 1.5 0 11-2-1.415V2.5a.5.5 0 011 0v8.585A1.5 1.5 0 015 12.5z"/></svg>`,
      clock: `<svg class="w-3.5 h-3.5 ${colorClass}" fill="none" stroke="currentColor" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" stroke-width="1.5"/><path d="M8 4v4l2 2" stroke-width="1.5"/></svg>`,
      lightning: `<svg class="w-3.5 h-3.5 ${colorClass}" fill="currentColor" viewBox="0 0 16 16"><path d="M11.251.068a.5.5 0 01.227.58L9.677 6.5H13a.5.5 0 01.364.843l-8 8.5a.5.5 0 01-.842-.49L6.323 9.5H3a.5.5 0 01-.364-.843l8-8.5a.5.5 0 01.615-.09z"/></svg>`,
      battery: `<svg class="w-3.5 h-3.5 ${colorClass}" fill="currentColor" viewBox="0 0 16 16"><path d="M2 6h5v4H2V6z"/><path d="M2 4a2 2 0 00-2 2v4a2 2 0 002 2h5a2 2 0 002-2V6a2 2 0 00-2-2H2z"/></svg>`
    }
    return icons[type] || icons.speedometer
  }

  formatDriverName(driver) {
    if (!driver || driver === 'ERROR_OUT_OF_LEN' || driver === '00000' || driver.trim() === '') {
      return this.t('driver_none')
    }
    return driver
  }

  formatOdometer(odometer) {
    if (!odometer) return '-'
    const kilometers = odometer / 1000.0
    
    if (kilometers >= 1000) {
      // Para valores acima de 1000km, usar formato de milhares com M
      const thousands = (kilometers / 1000).toFixed(1)
      return `${thousands}M`
    } else {
      // Para valores menores que 1000km, mostrar com 1 casa decimal
      return kilometers.toFixed(1)
    }
  }

  getOdometerLabel(odometer) {
    // Sempre mostrar KM embaixo
    return 'KM'
  }

  formatNumber(value) {
    if (!value) return '0'
    const num = parseFloat(value)
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toLocaleString('pt-BR')
  }

  // Métodos de formatação para o popup (números completos)
  formatRpmForPopup(value) {
    if (!value) return '0'
    const num = parseFloat(value)
    return num.toLocaleString('pt-BR')
  }

  formatOdometerForPopup(value) {
    if (!value) return '0'
    
    // O valor já vem em km do backend
    const kilometers = parseFloat(value)
    
    // Para números grandes, usar formatação brasileira com pontos para milhares e vírgula para decimal
    return kilometers.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })
  }

  formatHorimeterForPopup(value) {
    if (!value) return '0'
    const num = parseFloat(value)
    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })
  }

  getTemperatureColorClasses(temperature) {
    const temp = parseFloat(temperature) || 0
    if (temp >= 80) {
      return { icon: 'text-red-600 dark:text-red-400', text: 'text-red-700 dark:text-red-300' }
    } else if (temp >= 60) {
      return { icon: 'text-orange-600 dark:text-orange-400', text: 'text-orange-700 dark:text-orange-300' }
    } else if (temp >= 40) {
      return { icon: 'text-yellow-600 dark:text-yellow-400', text: 'text-yellow-700 dark:text-yellow-300' }
    } else {
      return { icon: 'text-blue-600 dark:text-blue-400', text: 'text-blue-700 dark:text-blue-300' }
    }
  }

  getBatteryColorClass(battery) {
    const level = parseFloat(battery) || 0
    if (level > 60) return 'text-green-600 dark:text-green-400'
    if (level > 30) return 'text-yellow-600 dark:text-yellow-400'
    return 'text-red-600 dark:text-red-400'
  }

  getBatteryGradient(battery) {
    const level = parseFloat(battery) || 0
    if (level > 60) return 'linear-gradient(to right, #34d399, #10b981)'
    if (level > 30) return 'linear-gradient(to right, #fbbf24, #f59e0b)'
    return 'linear-gradient(to right, #f87171, #ef4444)'
  }

  getTemperatureBgClass(temperature) {
    const temp = parseFloat(temperature) || 0
    if (temp >= 80) {
      return 'bg-red-100 dark:bg-red-900/30'
    } else if (temp >= 60) {
      return 'bg-orange-100 dark:bg-orange-900/30'
    } else if (temp >= 40) {
      return 'bg-yellow-100 dark:bg-yellow-900/30'
    } else {
      return 'bg-blue-100 dark:bg-blue-900/30'
    }
  }

  getBatteryBgClass(battery) {
    const level = parseFloat(battery) || 0
    if (level > 60) return 'bg-green-100 dark:bg-green-900/30'
    if (level > 30) return 'bg-yellow-100 dark:bg-yellow-900/30'
    return 'bg-red-100 dark:bg-red-900/30'
  }

  isMapRelatedElement(element) {
    // Verifica se o elemento clicado é relacionado ao Google Maps
    const mapSelectors = [
      '.gmnoprint',
      '.gm-style',
      '.gm-control-active',
      '.gm-bundled-control',
      '[role="button"]',
      'area[coords]'
    ]
    
    // Verificar o próprio elemento e seus ancestrais
    let currentElement = element
    while (currentElement && currentElement !== document.body) {
      // Verificar classes e atributos
      for (const selector of mapSelectors) {
        if (currentElement.matches && currentElement.matches(selector)) {
          return true
        }
      }
      
      // Verificar se é um elemento SVG do Google Maps
      if (currentElement.tagName === 'svg' || currentElement.tagName === 'path') {
        const parent = currentElement.closest('[data-value], .gmnoprint, .gm-style')
        if (parent) return true
      }
      
      currentElement = currentElement.parentElement
    }
    
    return false
  }

  destroy() {
    // Limpar todos os event listeners
    if (this.currentClickHandler) {
      document.removeEventListener('click', this.currentClickHandler)
      this.currentClickHandler = null
    }
    
    this.closeAllBalloons()
    if (this.balloonContainer && document.body.contains(this.balloonContainer)) {
      document.body.removeChild(this.balloonContainer)
      this.balloonContainer = null
    }
  }
}